<template>
  <expandable-list id="industry_proposal_expand_card" dense>
    <template v-slot:header>
      <div class="offer-schedule-item-container">
        <div class="content-group-1">
          <div class="name-segment-row">
            <span class="offer-schedule-name">{{ offerSchedule.name }}</span>
            <custom-chip bold>{{ getSegmentationLabel }}</custom-chip>
          </div>
          <trending-up :offer-sales="getOfferSalesFromOfferSchedule" :offer-schedule-type="offerSchedule.types[0]" />
        </div>

        <div class="content-group-2">
          <div class="industry-type-row">
            <industry-chip :industry="offerSchedule.industry" />
            <custom-chip bold>{{ getOfferScheduleTypeLabel }}</custom-chip>
          </div>
          <custom-chip>
            <span>De </span>
            <strong>{{ getOfferScheduleStartDate }}</strong>
            <span> até </span>
            <strong>{{ getOfferScheduleEndDate }}</strong>
          </custom-chip>
        </div>

        <div class="content-group-3">
          <mf-button
            id="industry_proposal_view_details"
            class="details-button"
            label="Ver detalhes"
            outlined
            :block="isMobile"
            @click.native.stop="goToDetails"
          />
          <span class="proposal-deadline-text">
            <span>Aceite até </span>
            <strong>{{ getProposalDeadlineDate }}</strong>
          </span>
          <div v-if="deadlineRunningOut" :class="{ 'mb-2': isMobile }">
            <mf-icon small color="vermelho-0" fill icon="warning" />
            <span class="proposal-deadline-warning">{{ deadlineRunningOutMessage }}</span>
          </div>
        </div>

        <div v-if="!isMobile" class="context-menu">
          <v-menu bottom origin="center center" transition="scale-transition">
            <template v-slot:activator="{ on }">
              <mf-button type="icon" icon="more_vert" color="cinza-0" v-on:click.native="on.click" />
            </template>
            <v-list>
              <export-offer-sales-modal
                :offer-sales-id="offerSchedule._id"
                is-industry-campaign
                :is-mercapromo-campaign="offerSchedule.industry.is_mercapromo"
              />
              <v-list-item @click="logsDialog = true">
                <v-list-item-title>Logs</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <logs-modal v-model="logsDialog" tags="OFFERS" logTitle="gestão de ofertas" :idItem="offerSchedule._id" />
      </div>
    </template>
    <template v-slot:content>
      <offer-sales-cross-sell
        v-if="offerSchedule.types[0] === 'crosssell'"
        :offer-sales="getOfferSalesFromOfferSchedule"
        preview-mode
        @goToDetails="goToDetails"
      />

      <offer-sales-digital-voucher
        v-else-if="offerSchedule.types[0] === 'dynamic_rule_offer_sales'"
        :offer-sales="getOfferSalesFromOfferSchedule"
        :is-products-group-preview="isProductsGroup"
        preview-mode
        @goToDetails="goToDetails"
      />

      <offer-sales-common
        v-else
        :offer-sales="getOfferSalesFromOfferSchedule"
        :currentOfferTypeKeyName="currentOfferTypeKeyName"
        :is-products-group-preview="isProductsGroup"
        :type="offerSchedule.types[0]"
        preview-mode
        @goToDetails="goToDetails"
      />
    </template>
  </expandable-list>
</template>

<script>
import { DateTime } from 'luxon'
import { getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'
import ExportOfferSalesModal from '@/modules/campanhas/gestao-ofertas-v2/views/components/ExportOfferSalesModal.vue'
import LogsModal from '@/components/logs-modal/LogsModal.vue'
import ExpandableList from '../ExpandableList.vue'
import OfferSalesCommon from '../offer-sales/OfferSalesCommon.vue'
import OfferSalesCrossSell from '../offer-sales/cross-sell/OfferSalesCrossSell.vue'
import OfferSalesDigitalVoucher from '../offer-sales/OfferSalesDigitalVoucher.vue'
import CustomChip from '@/modules/industria/ofertas-trade/views/components/offer-schedule-list/CustomChip.vue'
import TrendingUp from './TrendingUp.vue'
import IndustryChip from './IndustryChip.vue'

export default {
  name: 'OfferScheduleListItem',
  components: {
    ExportOfferSalesModal,
    LogsModal,
    ExpandableList,
    OfferSalesCommon,
    OfferSalesCrossSell,
    OfferSalesDigitalVoucher,
    IndustryChip,
    TrendingUp,
    CustomChip,
  },
  props: {
    offerSchedule: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    logsDialog: false,
    isOpen: false,
  }),
  computed: {
    getSegmentationLabel() {
      return this.offerSchedule.industry.is_mercapromo ? 'Campanha Massiva' : this.offerSchedule.segments.length ? 'Campanha Segmentada' : 'Campanha Clube'
    },
    getOfferScheduleStartDate() {
      return DateTime.fromISO(this.offerSchedule.start_date).toFormat('dd/MM/yyyy')
    },
    getOfferScheduleEndDate() {
      return DateTime.fromISO(this.offerSchedule.end_date).toFormat('dd/MM/yyyy')
    },
    getProposalDeadlineDate() {
      return DateTime.fromISO(this.offerSchedule.industry.proposal_deadline).toFormat('dd/MM/yyyy')
    },
    getOfferScheduleTypeLabel() {
      return this.offerSchedule.types > 1 ? 'Múltiplos' : getProductFormatTypeLabel(this.offerSchedule.types[0])
    },
    deadlineDifferenceInDays() {
      const proposalDeadline = this.offerSchedule?.industry?.proposal_deadline
      return Math.floor(DateTime.fromISO(proposalDeadline).diff(DateTime.now(), 'days').days)
    },
    deadlineRunningOut() {
      return this.deadlineDifferenceInDays <= 5
    },
    deadlineRunningOutMessage() {
      let message = ''
      if (this.deadlineDifferenceInDays === 1) {
        message = 'Resta 1 dia'
      } else if (this.deadlineDifferenceInDays < 1) {
        return 'Termina hoje'
      } else {
        message = `Restam ${this.deadlineDifferenceInDays} dias`
      }
      if (this.isMobile) {
        message += ' para o fim da adesão'
      }
      return message
    },
    currentOfferTypeKeyName() {
      switch (this.offerSchedule.types[0]) {
        case 'takepay':
          return 'takepay_offer_sales'
        case 'general':
          return 'offer_sales'
        case 'percentage':
          return 'percentage_offer_sales'
        case 'next_buy_cashback':
          return 'next_buy_cashback_offer_sales'
        case 'cashback_absolute':
          return 'cashback_absolute_offer_sales'
        case 'dynamic_rule_offer_sales':
          return 'dynamic_rule_offer_sales'
        case 'crosssell':
          return 'cross_sell_offer_sales'
        default:
          return ''
      }
    },
    isProductsGroup() {
      if (this.offerSchedule.types[0] === 'dynamic_rule_offer_sales') {
        return Boolean(this.offerSchedule[this.currentOfferTypeKeyName][0]?.trigger_products[0]?.group_id)
      } else if (this.offerSchedule.types[0] === 'crosssell') {
        return false
      } else {
        return Boolean(this.offerSchedule[this.currentOfferTypeKeyName][0][this.currentOfferTypeKeyName])
      }
    },
    getOfferSalesFromOfferSchedule() {
      return this.offerSchedule?.[this.currentOfferTypeKeyName] || []
    },
  },
  methods: {
    goToDetails() {
      const route = { name: 'ofertas-trade-visualizar', params: { id: this.offerSchedule._id } }
      this.$router.push(route).catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
.offer-schedule-item-container {
  padding: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;

  @media screen and (max-width: $xs-breakpoint) {
    gap: 28px;
  }

  .content-group-1 {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 16px;
    overflow: hidden;
    width: 50%;

    @media screen and (max-width: $xs-breakpoint) {
      align-items: center;
      width: 100%;
    }

    .name-segment-row {
      display: flex;
      align-items: center;
      gap: 16px;
      width: 90%;

      @media screen and (max-width: $xs-breakpoint) {
        flex-wrap: wrap;
        justify-content: center;
      }

      .offer-schedule-name {
        color: $cinza-d10;
        font-size: 1rem;
        font-weight: bold;
        overflow: hidden;
        text-overflow: ellipsis;
        order: 0;
        text-align: left;

        @media screen and (max-width: $xs-breakpoint) {
          white-space: normal;
          order: 1;
          text-align: center;
        }
      }
    }
  }

  .content-group-2 {
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 25%;

    @media screen and (max-width: $xs-breakpoint) {
      align-items: center;
    }

    .industry-type-row {
      display: flex;
      align-items: center;
      gap: 16px;
    }
  }

  .content-group-3 {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
    flex: unset;

    @media screen and (max-width: $xs-breakpoint) {
      align-items: center;
      text-align: center;
      flex: 100%;
    }

    .details-button {
      order: 0;
      width: fit-content;

      @media screen and (max-width: $xs-breakpoint) {
        order: 2;
        width: 100%;
      }
    }

    .proposal-deadline-text {
      margin-top: 12px;
      font-size: 14px;
      color: $cinza-d10;
    }

    .proposal-deadline-warning {
      font-size: 12px;
      font-weight: bold;
      color: $vermelho-0;
    }
  }

  .context-menu {
    align-self: flex-start;
  }
}
</style>
